import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Seo from "@/shop/Seo";
import { useCart } from "@/shop/CartContext";
import { apiSend, fmt, track } from "@/shop/api";

const COUNTRIES = [
  "IT", "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE",
  "GR", "HU", "IE", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE",
];
const COUNTRY_NAMES = {
  IT: "Italia", AT: "Austria", BE: "Belgio", BG: "Bulgaria", HR: "Croazia",
  CY: "Cipro", CZ: "Rep. Ceca", DK: "Danimarca", EE: "Estonia", FI: "Finlandia",
  FR: "Francia", DE: "Germania", GR: "Grecia", HU: "Ungheria", IE: "Irlanda",
  LV: "Lettonia", LT: "Lituania", LU: "Lussemburgo", MT: "Malta", NL: "Paesi Bassi",
  PL: "Polonia", PT: "Portogallo", RO: "Romania", SK: "Slovacchia",
  SI: "Slovenia", ES: "Spagna", SE: "Svezia",
};

const input = "w-full bg-ink border border-ink-border rounded-lg px-4 py-3 text-sm text-bone focus:outline-none focus:border-ember transition-colors";

export default function CheckoutPage() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    first_name: "", surname: "", email: "", phone: "",
    country: "IT", vat_info: "",
    line1: "", line2: "", city: "", postal_code: "", region: "",
    shipping_same: true,
    s_line1: "", s_line2: "", s_city: "", s_postal_code: "",
    notes: "", newsletter_opt_in: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const requiresShippingAddress = () => {
    if (form.shipping_same) return false;
    return !form.s_line1 || !form.s_city || !form.s_postal_code;
  };
  const valid =
    form.first_name && form.surname && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) &&
    form.line1 && form.city && form.postal_code && !requiresShippingAddress();

  const submit = async (e) => {
    e.preventDefault();
    if (sending || !valid) return;
    setSending(true);
    try {
      const res = await apiSend("/api/checkout/session", {
        items: items.map((i) => ({ sku: i.sku, qty: i.qty })),
        origin_url: window.location.origin,
        email: form.email,
        first_name: form.first_name,
        surname: form.surname,
        phone: form.phone,
        country: form.country,
        address: {
          line1: form.line1, line2: form.line2, city: form.city,
          postal_code: form.postal_code, region: form.region, country: form.country,
        },
        shipping_same: form.shipping_same,
        shipping_address: form.shipping_same
          ? {}
          : {
              line1: form.s_line1, line2: form.s_line2, city: form.s_city,
              postal_code: form.s_postal_code, country: form.country,
            },
        vat_info: form.vat_info,
        notes: form.notes,
        newsletter_opt_in: form.newsletter_opt_in,
      });
      track("begin_checkout", { value: subtotal, items: items.length });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.data?.detail || "Errore, riprova.");
      setSending(false);
    }
  };

  if (items.length === 0 && !sending)
    return (
      <div className="bg-ink text-bone min-h-screen flex flex-col items-center justify-center gap-4">
        <Seo title="Checkout — Stillwater" />
        <p className="font-serif italic text-2xl">Non c'è niente da pagare, per ora.</p>
        <Link to="/shop" className="font-mono text-[11px] uppercase tracking-[0.25em] text-ember hover:underline">
          Visita lo Shop
        </Link>
      </div>
    );

  return (
    <div className="bg-ink text-bone min-h-screen" data-testid="checkout-page">
      <Seo title="Checkout — Stillwater" description="Checkout sicuro Stillwater." />
      <header className="px-5 md:px-10 pt-24 md:pt-28 pb-8 border-b border-ink-border">
        <h1 className="font-serif font-light text-3xl md:text-5xl tracking-tight">
          <span className="italic text-ember">Checkout</span> sicuro
        </h1>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-faint">
          Il pagamento viene elaborato da Stripe — carte, Apple Pay, Google Pay
        </p>
      </header>

      <form onSubmit={submit} className="px-5 md:px-10 py-10 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-12 max-w-6xl">
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Nome</label>
              <input data-testid="co-first-name" className={input} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
            </div>
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Cognome</label>
              <input data-testid="co-surname" className={input} value={form.surname} onChange={(e) => set("surname", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Email</label>
              <input data-testid="co-email" type="email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Telefono</label>
              <input data-testid="co-phone" type="tel" className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
          </div>

          <p className="pt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Indirizzo di fatturazione</p>
          <input data-testid="co-line1" placeholder="Indirizzo e numero" className={input} value={form.line1} onChange={(e) => set("line1", e.target.value)} />
          <input data-testid="co-line2" placeholder="Complemento (opzionale)" className={input} value={form.line2} onChange={(e) => set("line2", e.target.value)} />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <input data-testid="co-postal" placeholder="CAP" className={input} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
            <input data-testid="co-city" placeholder="Città" className={input} value={form.city} onChange={(e) => set("city", e.target.value)} />
            <input data-testid="co-region" placeholder="Provincia (opz.)" className={input} value={form.region} onChange={(e) => set("region", e.target.value)} />
          </div>
          <div>
            <label className="block font-mono text-[9px] uppercase tracking-[0.2em] text-faint mb-2">Paese</label>
            <select data-testid="co-country" className={input} value={form.country} onChange={(e) => set("country", e.target.value)}>
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>{COUNTRY_NAMES[c]}</option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-3 pt-2 cursor-pointer">
            <input data-testid="co-shipping-same" type="checkbox" checked={form.shipping_same} onChange={(e) => set("shipping_same", e.target.checked)} className="accent-[#E25E2B]" />
            <span className="text-sm text-mist">Spedizione sullo stesso indirizzo</span>
          </label>

          {!form.shipping_same && (
            <div className="space-y-4 border border-ink-border rounded-lg p-5 bg-ink-surface" data-testid="shipping-address-block">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Indirizzo di spedizione</p>
              <input data-testid="co-s-line1" placeholder="Indirizzo e numero" className={input} value={form.s_line1} onChange={(e) => set("s_line1", e.target.value)} />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="co-s-postal" placeholder="CAP" className={input} value={form.s_postal_code} onChange={(e) => set("s_postal_code", e.target.value)} />
                <input data-testid="co-s-city" placeholder="Città" className={input} value={form.s_city} onChange={(e) => set("s_city", e.target.value)} />
              </div>
            </div>
          )}

          <input data-testid="co-vat" placeholder="P.IVA / ragione sociale (opzionale)" className={input} value={form.vat_info} onChange={(e) => set("vat_info", e.target.value)} />
          <textarea data-testid="co-notes" placeholder="Note per la spedizione (opzionale)" rows={2} className={input} value={form.notes} onChange={(e) => set("notes", e.target.value)} />

          <label className="flex items-start gap-3 cursor-pointer pt-2" data-testid="newsletter-optin-row">
            <input
              data-testid="co-newsletter"
              type="checkbox"
              checked={form.newsletter_opt_in}
              onChange={(e) => set("newsletter_opt_in", e.target.checked)}
              className="mt-1 accent-[#E25E2B]"
            />
            <span className="text-sm text-mist">
              Ricevi lo Stillwater Journal via email — opzionale, non selezionata di default. Disiscrizione con un click.
            </span>
          </label>
        </div>

        <aside className="h-fit lg:sticky lg:top-28 border border-ink-border bg-ink-surface p-6 md:p-8">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">Il tuo ordine</h2>
          <div className="mt-5 space-y-3">
            {items.map((i) => (
              <div key={i.sku} className="flex justify-between gap-4 text-sm">
                <span className="text-mist">{i.name} × {i.qty}</span>
                <span className="font-mono">{fmt(i.price * i.qty)}</span>
              </div>
            ))}
            <div className="flex justify-between gap-4 pt-3 border-t border-ink-border text-sm">
              <span className="text-mist">Spedizione</span>
              <span className="font-mono text-xs" data-testid="checkout-shipping">calcolata dopo la conferma</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-ink-border">
              <span className="font-serif italic text-lg">Totale</span>
              <span className="font-mono text-lg" data-testid="checkout-total">{fmt(subtotal)}+</span>
            </div>
          </div>
          <button
            data-testid="pay-button"
            type="submit"
            disabled={!valid || sending}
            className="mt-8 w-full bg-ember text-ink font-mono text-[11px] uppercase tracking-[0.25em] px-6 py-4 rounded-full hover:bg-ember-bright transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? "Connessione a Stripe…" : "Vai al pagamento"}
          </button>
          <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-faint text-center">
            IVA inclusa — pagine di pagamento gestite da Stripe
          </p>
        </aside>
      </form>
    </div>
  );
}
